import { Link } from "react-router-dom";
import { Heart, UserCircle, Lightbulb, Target, Quote, Sparkles } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { SITE } from "@/lib/site";

/* ------------------------------------------------------------------ */
/* Custom SVG illustrations — one per pillar.                          */
/* No raster images, no AI photos. Pure brand-aligned geometry.        */
/* ------------------------------------------------------------------ */

// Reusable subtle dot pattern background
const DotPattern = ({ id }) => (
  <pattern id={id} x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
    <circle cx="1" cy="1" r="1" fill="currentColor" opacity="0.18" />
  </pattern>
);

// 1. Compassion First — two soft arches embracing a heart/pulse beat
function CompassionArt() {
  return (
    <svg viewBox="0 0 320 200" className="w-full h-full" aria-hidden="true">
      <defs>
        <linearGradient id="cf-grad" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="hsl(188 78% 50%)" />
          <stop offset="100%" stopColor="hsl(150 55% 45%)" />
        </linearGradient>
        <DotPattern id="cf-dots" />
      </defs>
      <rect width="320" height="200" fill="url(#cf-dots)" className="text-primary" />
      {/* Two overlapping arcs forming an embrace */}
      <path d="M 60 150 Q 60 70 160 70 Q 260 70 260 150" stroke="url(#cf-grad)" strokeWidth="3" fill="none" opacity="0.45" />
      <path d="M 90 150 Q 90 95 160 95 Q 230 95 230 150" stroke="url(#cf-grad)" strokeWidth="3" fill="none" opacity="0.7" />
      <path d="M 120 150 Q 120 120 160 120 Q 200 120 200 150" stroke="url(#cf-grad)" strokeWidth="3" fill="none" />
      {/* Heart pulse line at the center */}
      <path
        d="M 70 150 L 110 150 L 124 130 L 142 168 L 162 110 L 182 168 L 200 130 L 214 150 L 250 150"
        stroke="hsl(188 78% 32%)"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Tiny heart marker */}
      <path
        d="M160 60 c -3 -4 -8 -2 -8 2 c 0 4 8 8 8 8 c 0 0 8 -4 8 -8 c 0 -4 -5 -6 -8 -2 z"
        fill="hsl(150 55% 45%)"
      />
    </svg>
  );
}

// 2. Collaboration — two nodes connected by a flowing path, orbital dots
function CollaborationArt() {
  return (
    <svg viewBox="0 0 320 200" className="w-full h-full" aria-hidden="true">
      <defs>
        <DotPattern id="co-dots" />
        <linearGradient id="co-grad" x1="0" x2="1">
          <stop offset="0%" stopColor="hsl(188 78% 35%)" />
          <stop offset="100%" stopColor="hsl(150 55% 40%)" />
        </linearGradient>
      </defs>
      <rect width="320" height="200" fill="url(#co-dots)" className="text-primary" />
      {/* Connection arc */}
      <path d="M 80 100 C 130 30, 190 170, 240 100" stroke="url(#co-grad)" strokeWidth="2.5" fill="none" strokeDasharray="0" />
      <path d="M 80 100 C 130 30, 190 170, 240 100" stroke="hsl(188 78% 32%)" strokeWidth="1" fill="none" strokeDasharray="3 6" opacity="0.4" />
      {/* Left node */}
      <circle cx="80" cy="100" r="22" fill="hsl(188 78% 32%)" />
      <circle cx="80" cy="100" r="34" fill="none" stroke="hsl(188 78% 32%)" strokeWidth="1.5" opacity="0.35" />
      <circle cx="80" cy="100" r="46" fill="none" stroke="hsl(188 78% 32%)" strokeWidth="1" opacity="0.18" />
      {/* Right node */}
      <circle cx="240" cy="100" r="22" fill="hsl(150 55% 38%)" />
      <circle cx="240" cy="100" r="34" fill="none" stroke="hsl(150 55% 38%)" strokeWidth="1.5" opacity="0.35" />
      <circle cx="240" cy="100" r="46" fill="none" stroke="hsl(150 55% 38%)" strokeWidth="1" opacity="0.18" />
      {/* Orbiting small dots */}
      <circle cx="160" cy="50" r="3.5" fill="hsl(150 55% 45%)" />
      <circle cx="160" cy="155" r="3.5" fill="hsl(188 78% 38%)" />
      <circle cx="195" cy="78" r="2.5" fill="hsl(188 78% 38%)" opacity="0.6" />
      <circle cx="125" cy="125" r="2.5" fill="hsl(150 55% 45%)" opacity="0.6" />
    </svg>
  );
}

// 3. Holistic Approach — concentric organic rings (mind, body, environment)
function HolisticArt() {
  return (
    <svg viewBox="0 0 320 200" className="w-full h-full" aria-hidden="true">
      <defs>
        <DotPattern id="hl-dots" />
        <radialGradient id="hl-glow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="hsl(150 55% 45%)" stopOpacity="0.4" />
          <stop offset="100%" stopColor="hsl(150 55% 45%)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="320" height="200" fill="url(#hl-dots)" className="text-primary" />
      <ellipse cx="160" cy="100" rx="120" ry="80" fill="url(#hl-glow)" />
      {/* Concentric rings */}
      <ellipse cx="160" cy="100" rx="100" ry="68" fill="none" stroke="hsl(188 78% 32%)" strokeWidth="1" opacity="0.28" />
      <ellipse cx="160" cy="100" rx="74" ry="52" fill="none" stroke="hsl(188 78% 32%)" strokeWidth="1.5" opacity="0.55" />
      <ellipse cx="160" cy="100" rx="48" ry="34" fill="none" stroke="hsl(188 78% 32%)" strokeWidth="2" />
      <ellipse cx="160" cy="100" rx="22" ry="16" fill="hsl(150 55% 45%)" opacity="0.85" />
      {/* Cross-hairs */}
      <line x1="160" y1="20" x2="160" y2="180" stroke="hsl(188 78% 32%)" strokeWidth="0.8" opacity="0.18" strokeDasharray="2 4" />
      <line x1="40" y1="100" x2="280" y2="100" stroke="hsl(188 78% 32%)" strokeWidth="0.8" opacity="0.18" strokeDasharray="2 4" />
      {/* Layer labels (visual rhythm) */}
      <circle cx="160" cy="32" r="3" fill="hsl(188 78% 32%)" />
      <circle cx="160" cy="48" r="2" fill="hsl(188 78% 32%)" opacity="0.6" />
      <circle cx="50" cy="100" r="3" fill="hsl(150 55% 45%)" />
      <circle cx="270" cy="100" r="3" fill="hsl(150 55% 45%)" />
      <circle cx="160" cy="168" r="3" fill="hsl(188 78% 32%)" />
    </svg>
  );
}

// 4. Empowerment — radial sunburst from a seed, ascending lines
function EmpowermentArt() {
  return (
    <svg viewBox="0 0 320 200" className="w-full h-full" aria-hidden="true">
      <defs>
        <DotPattern id="em-dots" />
        <linearGradient id="em-ray" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="hsl(188 78% 32%)" stopOpacity="0.05" />
          <stop offset="100%" stopColor="hsl(188 78% 32%)" stopOpacity="0.7" />
        </linearGradient>
      </defs>
      <rect width="320" height="200" fill="url(#em-dots)" className="text-primary" />
      {/* Ground line */}
      <line x1="20" y1="170" x2="300" y2="170" stroke="hsl(188 78% 32%)" strokeWidth="1" opacity="0.25" />
      {/* Rays radiating from base center */}
      {[-60, -45, -30, -15, 0, 15, 30, 45, 60].map((deg, i) => (
        <line
          key={i}
          x1="160"
          y1="170"
          x2={160 + Math.sin((deg * Math.PI) / 180) * 160}
          y2={170 - Math.cos((deg * Math.PI) / 180) * 160}
          stroke="url(#em-ray)"
          strokeWidth={i === 4 ? 2 : 1.2}
          strokeLinecap="round"
        />
      ))}
      {/* Ascending dots on the central ray */}
      <circle cx="160" cy="142" r="3" fill="hsl(150 55% 45%)" />
      <circle cx="160" cy="110" r="3.5" fill="hsl(150 55% 45%)" />
      <circle cx="160" cy="74" r="4" fill="hsl(150 55% 45%)" />
      {/* Seed at the base */}
      <circle cx="160" cy="170" r="9" fill="hsl(188 78% 32%)" />
      <circle cx="160" cy="170" r="14" fill="none" stroke="hsl(188 78% 32%)" strokeWidth="1" opacity="0.4" />
      {/* Top apex */}
      <path d="M 152 38 L 160 22 L 168 38 Z" fill="hsl(150 55% 45%)" />
    </svg>
  );
}

const PILLARS = [
  {
    icon: Heart,
    title: "Compassion First",
    body: "A safe, judgment-free space — every visit, every time.",
    art: CompassionArt,
    accent: "hsl(188 78% 32%)",
  },
  {
    icon: UserCircle,
    title: "Collaboration",
    body: "You're an active partner. We plan care together.",
    art: CollaborationArt,
    accent: "hsl(188 78% 32%)",
  },
  {
    icon: Lightbulb,
    title: "Holistic Approach",
    body: "Mind, body, relationships, and environment — all of it matters.",
    art: HolisticArt,
    accent: "hsl(150 55% 38%)",
  },
  {
    icon: Target,
    title: "Empowerment",
    body: "Confidence and coping skills that last beyond the visit.",
    art: EmpowermentArt,
    accent: "hsl(150 55% 38%)",
  },
];

const TESTIMONIALS = [
  { quote: "I finally feel heard and understood. The care helped me and my family find hope again.", author: "Parent of a teen patient" },
  { quote: "The holistic approach made all the difference. It's not just medication — it's the whole picture.", author: "Adult patient" },
  { quote: "My child is thriving now. Compassionate care and ongoing support have been life-changing.", author: "Parent of a child with ADHD" },
];

const APPROACH = [
  "Evidence-based medication",
  "Practical coping strategies",
  "Education on diagnosis & options",
  "Therapist & PCP coordination",
  "Family support when helpful",
];

export default function Philosophy() {
  return (
    <div className="min-h-screen">
      <Navigation />

      {/* HERO */}
      <section className="relative pt-28 md:pt-32 pb-10 md:pb-14 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/philosophy-background.jpg"
            alt=""
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/85 to-background" />
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-medium mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Our principles
            </div>
            <h1 data-testid="philosophy-title" className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-balance">
              Philosophy <em className="not-italic gradient-text font-light">of care.</em>
            </h1>
          </div>

          <div className="max-w-4xl mx-auto rounded-3xl border border-border bg-card/60 backdrop-blur-md p-6 md:p-10">
            <p className="text-lg md:text-xl leading-relaxed text-pretty">
              Healing begins with being <strong className="text-primary">truly heard.</strong> We take a holistic view of mental health — beyond symptoms, into the whole person. Medication has its place, but it&rsquo;s never the whole story.
            </p>
          </div>
        </div>
      </section>

      <main className="pb-8 md:pb-12">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">

            {/* Pillars — editorial magazine style with custom SVG art */}
            <section className="mt-10 md:mt-14 mb-10 md:mb-14">
              <div
                data-testid="pillars-rail"
                className="-mx-4 px-4 md:mx-0 md:px-0 flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory pb-4 md:pb-0 md:overflow-visible md:grid md:grid-cols-2 no-scrollbar"
              >
                {PILLARS.map((p, i) => {
                  const Art = p.art;
                  const Icon = p.icon;
                  return (
                    <article
                      key={p.title}
                      data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                      className="snap-center shrink-0 w-[85%] sm:w-[65%] md:w-auto group relative rounded-3xl border border-border bg-card overflow-hidden transition-all duration-500 hover:border-primary/40 hover:shadow-[0_30px_60px_-30px_hsl(var(--primary)/0.25)]"
                    >
                      {/* Custom SVG illustration */}
                      <div className="relative h-48 md:h-52 overflow-hidden bg-gradient-to-br from-primary/[0.04] to-accent/[0.04]">
                        <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-[1.04]">
                          <Art />
                        </div>
                        {/* Editorial number — large serif behind */}
                        <span
                          aria-hidden="true"
                          className="absolute -top-3 left-5 font-display text-[7rem] leading-none font-light text-primary/[0.10] select-none"
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        {/* Icon chip */}
                        <span
                          className="absolute top-4 right-4 grid place-items-center w-11 h-11 rounded-2xl bg-card/95 backdrop-blur border border-border shadow-sm"
                          style={{ color: p.accent }}
                        >
                          <Icon className="w-5 h-5" strokeWidth={1.75} />
                        </span>
                      </div>

                      {/* Body */}
                      <div className="px-6 md:px-8 py-6 md:py-7 border-t border-border/60">
                        <div className="flex items-baseline gap-3 mb-2">
                          <span className="text-[11px] uppercase tracking-[0.22em] font-medium text-muted-foreground">
                            Pillar / {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="h-px flex-1 bg-border" />
                        </div>
                        <h3 className="font-display text-2xl md:text-[1.65rem] tracking-tight mb-2 group-hover:text-primary transition-colors">
                          {p.title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">{p.body}</p>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Mobile-only swipe hint */}
              <div className="md:hidden mt-3 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                <span className="h-px w-6 bg-border" />
                Swipe to explore
              </div>
            </section>

            {/* Testimonials */}
            <section className="mb-10 md:mb-14">
              <div className="text-center mb-10">
                <span className="grid place-items-center w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 text-primary mx-auto mb-4">
                  <Quote className="w-5 h-5" />
                </span>
                <h2 className="font-display text-3xl md:text-4xl tracking-tight">What families say</h2>
              </div>

              <div className="grid md:grid-cols-3 gap-5">
                {TESTIMONIALS.map((t, i) => (
                  <article
                    key={i}
                    data-testid={`testimonial-${i}`}
                    className="relative rounded-3xl border border-border bg-card/40 p-7"
                  >
                    <Quote className="w-8 h-8 text-primary/20 absolute top-5 right-5" />
                    <p className="leading-relaxed italic mb-5 text-foreground/85">&ldquo;{t.quote}&rdquo;</p>
                    <p className="text-primary text-sm font-semibold">— {t.author}</p>
                  </article>
                ))}
              </div>
            </section>

            {/* Beyond Medication */}
            <section className="mb-10 md:mb-14 rounded-3xl border border-border bg-card/40 p-6 md:p-10">
              <div className="text-center mb-8">
                <span className="grid place-items-center w-12 h-12 rounded-2xl bg-primary text-primary-foreground mx-auto mb-4">
                  <Heart className="w-5 h-5" />
                </span>
                <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-3">Beyond medication</h2>
                <p className="text-muted-foreground max-w-xl mx-auto">
                  A balanced approach that combines:
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {APPROACH.map((item, i) => (
                  <div
                    key={item}
                    className={`flex items-center gap-3 rounded-xl border border-border bg-background/40 px-5 py-4 ${
                      i === APPROACH.length - 1 ? "md:col-span-2 md:max-w-md md:mx-auto" : ""
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="text-center rounded-3xl border border-primary/30 bg-gradient-to-br from-card to-primary/15 p-6 md:p-12 relative overflow-hidden">
              <div className="glow-orb -top-20 -right-10 w-[320px] h-[320px] bg-primary/30" />
              <div className="relative z-10 max-w-xl mx-auto">
                <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-3">
                  Ready when you are.
                </h2>
                <Button
                  asChild
                  size="lg"
                  data-testid="philosophy-cta"
                  className="mt-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12 px-7"
                >
                  <Link to="/contact">Get in touch</Link>
                </Button>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
